import React, { useEffect, useState, useCallback } from "react";
import { SPWrapper } from "../MetricDashboard.styles";
import CountsView from "./CountsView";
import ComparisionView from "./ComparisionView";
import moment from "moment";
import FilterBlock from "./FilterBlock";
import { Loader } from "@vds/loaders";
import * as cfst_DashboardCount from "./CFSTdashboardCount";
import { cfstConstants } from "./constants";
import { cfstComparisonViewDropDownFilterValues, cfstDropDownFilterValues } from "../../../constants/DropdownConstants";

const initialLoadingState = {
  counts: false,
  deRe: false,
  comparison: false,
};

const CFSTView = () => {
  const [dashboardCountData, setDashboardCountData] = useState([]);
  const [dashboardComparisionData, setDashboardComparisionData] = useState([]);
  const [dashboardDeReData, setDashboardDeReData] = useState({});
  const [filterResponse, setFilterResponse] = useState({
    fromDate: cfstConstants.defaultMMDDYYYY,
    toDate: cfstConstants.defaultMMDDYYYY,
    cfstFilterValue: cfstDropDownFilterValues[1].value, // Default to "last_5_days"
    selectDates: { startDate: "", endDate: "" },
  });
  const [notificationCounts, setNotificationCounts] = useState(false);
  const [notificationDeRe, setNotificationDeRe] = useState(false);
  const [notificationComparison, setNotificationComparison] = useState(false);
  const [loading, setLoading] = useState(initialLoadingState);
  const [cfstComparisonViewFilterValue, setCfstComparisonViewFilterValue] = useState(cfstComparisonViewDropDownFilterValues[0].value);
  const [isFilterReset, setIsFilterReset] = useState(true);
  const [notification, setNotification] = useState({
    onlineJobs: [],
    offlineJobs: [],
  });
  const [jobTrendsNotification, setJobTrendsNotification] = useState([]);
  const [onlineVsOfflineJobsTrendNotification, setOnlineVsOfflineJobsTrendNotification] = useState(null);

  const filterDataValidation = useCallback((localFromDate, localToDate, localCfstFilterValue) => {
    if (localFromDate === cfstConstants.defaultMMDDYYYY && localToDate === cfstConstants.defaultMMDDYYYY) {
      const dateRange = {
        last_5_days: {
          fromDateForPayload: moment().subtract(4, cfstConstants.dateSplits.days).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_10_days: {
          fromDateForPayload: moment().subtract(9, cfstConstants.dateSplits.days).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_15_days: {
          fromDateForPayload: moment().subtract(14, cfstConstants.dateSplits.days).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_20_days: {
          fromDateForPayload: moment().subtract(19, cfstConstants.dateSplits.days).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_30_days: {
          fromDateForPayload: moment().subtract(29, cfstConstants.dateSplits.days).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_2_months: {
          fromDateForPayload: moment().subtract(2, cfstConstants.dateSplits.months).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_3_months: {
          fromDateForPayload: moment().subtract(3, cfstConstants.dateSplits.months).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_6_months: {
          fromDateForPayload: moment().subtract(6, cfstConstants.dateSplits.months).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_1_year: {
          fromDateForPayload: moment().subtract(1, cfstConstants.dateSplits.years).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        last_2_years: {
          fromDateForPayload: moment().subtract(2, cfstConstants.dateSplits.years).format(cfstConstants.defaultYYYYMMDD),
          toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD),
        },
        till_date: { fromDateForPayload: cfstConstants.tillDateValue, toDateForPayload: moment().format(cfstConstants.defaultYYYYMMDD) },
      };
      return dateRange[localCfstFilterValue];
    } else {
      return {
        fromDateForPayload: moment(localFromDate).format(cfstConstants.defaultYYYYMMDD),
        toDateForPayload: moment(localToDate).format(cfstConstants.defaultYYYYMMDD),
      };
    }
  }, []);

  const getDefaultDashboardCounts = () => ({
    cfst: [
      { id: 11, name: "Total Jobs", value: "--" },
      { id: 12, name: "Total Scheduled Jobs", value: "--" },
      { id: 13, name: "Total Open Jobs", value: "--" },
      { id: 14, name: "Total Abandoned Jobs", value: "--" },
      {
        id: 15,
        name: "Agent Incompletion Jobs",
        value: "--",
        tooltip: "Past scheduled date, which are yet to be marked as completed by the Agent.",
      },
      { id: 21, name: "Scheduled Jobs", value: "--" },
      { id: 22, name: "Rescheduled Jobs", value: "--" },
      { id: 23, name: "Completed Jobs", value: "--" },
      { id: 24, name: "Partially Completed Jobs", value: "--" },
      { id: 25, name: "Cancelled Jobs", value: "--" },
      { id: 31, name: "Total Transfers", value: "--" },
      { id: 32, name: "Total Removals", value: "--" },
      { id: 33, name: "Realized Revenue", value: "--" },
      { id: 34, name: "Contracted Revenue", value: "--" },
    ],
    nonCfst: [
      { id: 11, name: "Total Jobs", value: "--" },
      { id: 12, name: "Total Scheduled Jobs", value: "--" },
      { id: 13, name: "Total Open Jobs", value: "--" },
      { id: 14, name: "Total Abandoned Jobs", value: "--" },
      {
        id: 15,
        name: "Agent Incompletion Jobs",
        value: "--",
        tooltip: "Past scheduled date, which are yet to be marked as completed by the Agent.",
      },
      { id: 21, name: "Scheduled Jobs", value: "--" },
      { id: 22, name: "Rescheduled Jobs", value: "--" },
      { id: 23, name: "Completed Jobs", value: "--" },
      { id: 24, name: "Partially Completed Jobs", value: "--" },
      { id: 25, name: "Cancelled Jobs", value: "--" },
      { id: 31, name: "Realized Revenue", value: "--" },
    ],
  });

  const getDashboardCountsByType = useCallback((localData = {}, type = cfstConstants.cfst) => {
    const jobCounts = localData?.jobCounts?.[type] || {};
    const jobFees = localData?.jobFees?.[type] || {};
    const isCFST = type === cfstConstants.cfst;
    const openJobsPercentage = ((jobCounts.open / jobCounts.total) * 100).toFixed(2);
    const closedJobsPercentage = ((jobCounts.closed / jobCounts.total) * 100).toFixed(2);
    const agentIncompletionJobsPercentage = ((jobCounts.pastScheduledInProgress / jobCounts.total) * 100).toFixed(2);
    const baseCounts = [
      { id: 11, name: "Total Jobs", value: jobCounts.total },
      {
        id: 12,
        name: "Total Scheduled Jobs",
        value:
          Number(jobCounts.scheduled || 0) +
          Number(jobCounts.rescheduled || 0) +
          Number(jobCounts.completed || 0) +
          Number(jobCounts.partiallyCompleted || 0) +
          Number(jobCounts.cancelled || 0),
      },
      {
        id: 13,
        name: "Total Open Jobs",
        value: jobCounts.open,
        percentage: jobCounts.total !== "0" ? openJobsPercentage : 0,
        tooltip: `Total Open Jobs are ${openJobsPercentage}% of Total Jobs`,
        notification:
          openJobsPercentage >= 25 ? (
            <>
              Total Open Jobs are <b>{openJobsPercentage}%</b> of Total Jobs
            </>
          ) : null,
      },
      {
        id: 14,
        name: "Total Abandoned Jobs",
        value: jobCounts.closed,
        percentage: jobCounts.total !== "0" ? closedJobsPercentage : 0,
        tooltip: `Total Abandoned Jobs are ${closedJobsPercentage}% of Total Jobs`,
        notification:
          closedJobsPercentage >= 25 ? (
            <>
              Total Abandoned Jobs are <b>{closedJobsPercentage}%</b> of Total Jobs
            </>
          ) : null,
      },
      {
        id: 15,
        name: "Agent Incompletion Jobs",
        value: jobCounts.pastScheduledInProgress,
        // tooltip: "Past scheduled date, which are yet to be marked as completed by the Agent.",
        tooltip: `Agent Incompletion Jobs are ${agentIncompletionJobsPercentage}% of Total Jobs`,
        notification:
          agentIncompletionJobsPercentage >= 25 ? (
            <>
              Agent Incompletion Jobs are <b>{agentIncompletionJobsPercentage}%</b> of Total Jobs
            </>
          ) : null,
        percentage: jobCounts.total !== "0" ? agentIncompletionJobsPercentage : 0,
      },
      { id: 21, name: "Scheduled Jobs", value: jobCounts.scheduled },
      { id: 22, name: "Rescheduled Jobs", value: jobCounts.rescheduled },
      { id: 23, name: "Completed Jobs", value: jobCounts.completed },
      {
        id: 24,
        name: "Partially Completed Jobs",
        value: jobCounts.partiallyCompleted,
      },
      { id: 25, name: "Cancelled Jobs", value: jobCounts.cancelled },
    ];

    if (isCFST) {
      baseCounts.push(
        { id: 31, name: "Total Transfers", value: localData?.transfersCount },
        { id: 32, name: "Total Removals", value: localData?.removalsCount },
        { id: 33, name: "Realized Revenue", value: `$${jobFees.totalFees}` },
        {
          id: 34,
          name: "Contracted Revenue",
          value: `$${jobFees.estimatedCustomerFees}`,
        }
      );
    } else {
      baseCounts.push({
        id: 31,
        name: "Realized Revenue",
        value: `$${jobFees.totalFees}`,
      });
    }
    return baseCounts;
  }, []);

  const getDashboardCounts = useCallback(
    (localData = {}) => ({
      cfst: getDashboardCountsByType(localData, cfstConstants.cfst),
      nonCfst: getDashboardCountsByType(localData, cfstConstants.nonCfst),
    }),
    [getDashboardCountsByType]
  );

  const fetchDashboardData = useCallback(async () => {
    setLoading({ counts: true, deRe: true, comparison: false });
    setNotificationCounts(null);
    setNotificationDeRe(null);
    let onlineJobsNotification = [],
      offlineJobsNotification = [];
    const dateValidation = filterDataValidation(filterResponse.fromDate, filterResponse.toDate, filterResponse.cfstFilterValue);
    const reqBody = {
      startDate: `${dateValidation.fromDateForPayload} ${cfstConstants.fromTime}`,
      endDate: `${dateValidation.toDateForPayload} ${cfstConstants.toTime}`,
    };

    const [countsResult, deReResult] = await Promise.allSettled([
      cfst_DashboardCount.cfstDashboardCount(reqBody),
      cfst_DashboardCount.cfstDashboardDeReCasesCount(reqBody),
    ]);

    // Counts
    if (countsResult.status === "fulfilled" && countsResult.value.data.statusCode === "200" && countsResult.value.data.data) {
      const dashboardCount = getDashboardCounts(countsResult.value.data.data);
      setDashboardCountData(dashboardCount);
      onlineJobsNotification = dashboardCount.cfst.filter((item) => item.notification).map((item) => item.notification);
      offlineJobsNotification = dashboardCount.nonCfst.filter((item) => item.notification).map((item) => item.notification);
    } else {
      setNotificationCounts(true);
      setDashboardCountData(getDefaultDashboardCounts());
    }

    // De/Re
    if (deReResult.status === "fulfilled" && deReResult.value.data.statusCode === "200" && deReResult.value.data.data) {
      const caseData = { ...deReResult.value.data.data };
      const percentage = ((caseData.caseCount / caseData.totalCfstCaseCount) * 100).toFixed(2);
      if (percentage >= 25) {
        caseData.percentage = caseData.totalCfstCaseCount !== "0" ? percentage : "0";
        caseData.notification = (
          <>
            De/Re Cases are <b>{caseData.percentage}%</b> of Total CFST Cases
          </>
        );
        caseData.tooltip = `De/Re Cases are ${caseData.percentage}% of Total CFST Cases`;

        onlineJobsNotification.push(caseData.notification);
      }

      setDashboardDeReData(caseData);
    } else {
      setNotificationDeRe(true);
    }
    setNotification({
      onlineJobs: onlineJobsNotification,
      offlineJobs: offlineJobsNotification,
    });
    setLoading((prev) => ({ ...prev, counts: false, deRe: false }));
  }, [filterResponse]);

  const getJobTrendsNotification = (data) => {
    const prevJobData = data.slice(0, data.length - 2);
    const recentJobData = data.slice(data.length - 2, -1)[0];

    const previousJobData =
      prevJobData.length > 1
        ? {
            scheduled: prevJobData.reduce((acc, item) => acc + Number(item.scheduled), 0) / prevJobData.length,
            rescheduled: prevJobData.reduce((acc, item) => acc + Number(item.rescheduled), 0) / prevJobData.length,
            completed: prevJobData.reduce((acc, item) => acc + Number(item.completed), 0) / prevJobData.length,
            partiallyCompleted: prevJobData.reduce((acc, item) => acc + Number(item.partiallyCompleted), 0) / prevJobData.length,
          }
        : {
            scheduled: Number(prevJobData[0].scheduled),
            rescheduled: Number(prevJobData[0].rescheduled),
            completed: Number(prevJobData[0].completed),
            partiallyCompleted: Number(prevJobData[0].partiallyCompleted),
          };
    let notificationTypes = [
      {
        type: "scheduled",
        increaseText: "Scheduled job volume shows strong upward momentum",
        decreaseText: "Scheduled job volume shows declining trends",
        isFalsePositive: false,
      },
      {
        type: "rescheduled",
        increaseText: "Rescheduled job volume shows strong upward momentum",
        decreaseText: "Rescheduled job volume shows declining trends",
        isFalsePositive: true,
      },
      {
        type: "completed",
        increaseText: "The Rate of successfully completed jobs has markedly improved",
        decreaseText: "The Rate of successfully completed jobs has markedly declined",
        isFalsePositive: false,
      },
      {
        type: "partiallyCompleted",
        increaseText: "The Rate of partially completed jobs has markedly improved",
        decreaseText: "The Rate of partially completed jobs has markedly declined",
        isFalsePositive: true,
      },
    ];
    let notifications = [];
    for (const item of notificationTypes) {
      const { type, isFalsePositive, increaseText, decreaseText } = item;
      const period = cfstComparisonViewDropDownFilterValues.find((item) => item.value === cfstComparisonViewFilterValue)?.text;
      if (previousJobData[type] < recentJobData[type]) {
        notifications.push({ type: isFalsePositive ? "error" : "success", content: `${increaseText} ${period}` });
      } else if (previousJobData[type] > recentJobData[type]) {
        notifications.push({ type: isFalsePositive ? "success" : "error", content: `${decreaseText} ${period}` });
      }
    }
    setJobTrendsNotification(notifications);
  };

  const getOnlineVsOfflineTrendNotification = (cfstJobData, c360JobData) => {
    const onlineJobData = cfstJobData
      .map((item, index) => Object.values(item).reduce((acc, val) => acc + Number(val), 0))
      .reduce((acc, val) => acc + val, 0);
    const offlineJobData = c360JobData
      .map((item, index) => Object.values(item).reduce((acc, val) => acc + Number(val), 0))
      .reduce((acc, val) => acc + val, 0);
    const period = cfstComparisonViewDropDownFilterValues.find((item) => item.value === cfstComparisonViewFilterValue)?.text;
    setOnlineVsOfflineJobsTrendNotification({
      type: onlineJobData > offlineJobData ? "success" : "error",
      content: `The growth of ${onlineJobData > offlineJobData ? "online" : "offline"} jobs has significantly outpaced ${
        onlineJobData > offlineJobData ? "offline" : "online"
      } jobs ${period}`,
    });
  };

  // Comparison fetch
  const fetchComparisonData = useCallback(async () => {
    setLoading((prev) => ({ ...prev, comparison: true }));
    setNotificationComparison(false);

    const dateValidation = filterDataValidation(cfstConstants.defaultMMDDYYYY, cfstConstants.defaultMMDDYYYY, cfstComparisonViewFilterValue);
    const reqBody = {
      startDate:
        cfstComparisonViewFilterValue === cfstConstants.tillDateOption
          ? dateValidation.fromDateForPayload
          : `${dateValidation.fromDateForPayload} ${cfstConstants.fromTime}`,
      endDate: `${dateValidation.toDateForPayload} ${cfstConstants.toTime}`,
    };

    try {
      const response = await cfst_DashboardCount.cfstDashboardComparisonView(reqBody);
      if (response.data.statusCode === "200" && response.data.data) {
        setDashboardComparisionData(response.data.data);
        const cfstData = Object.values(response.data.data.monthWiseTrends.CFST);
        const C360Data = Object.values(response.data.data.monthWiseTrends.C360);
        if (cfstData.length > 2) {
          getJobTrendsNotification(cfstData);
        } else {
          setJobTrendsNotification([]);
        }
        const cfstJobData = cfstData.slice(0, cfstData.length);
        const c360JobData = C360Data.slice(0, C360Data.length);
        if (cfstJobData.length && c360JobData.length) {
          getOnlineVsOfflineTrendNotification(cfstJobData, c360JobData);
        } else {
          setOnlineVsOfflineJobsTrendNotification(null);
        }
      } else {
        setNotificationComparison(true);
      }
    } catch (err) {
      setNotificationComparison(true);
    } finally {
      setLoading((prev) => ({ ...prev, comparison: false }));
    }
  }, [cfstComparisonViewFilterValue]);

  useEffect(() => {
    if (isFilterReset) {
      fetchDashboardData();
      setIsFilterReset(false);
    }
  }, [isFilterReset]);

  useEffect(() => {
    fetchComparisonData();
  }, [fetchComparisonData]);

  return (
    <SPWrapper>
      <Loader active={loading.counts || loading.deRe || loading.comparison} fullscreen />
      <FilterBlock
        getDashboardCountsWithDeRe={fetchDashboardData}
        filterResponse={filterResponse}
        setFilterResponse={setFilterResponse}
        setIsFilterReset={setIsFilterReset}
      />
      <CountsView
        heading={cfstConstants.onlineSchedulesHeading}
        counts={dashboardCountData?.cfst}
        dashboardDeReData={dashboardDeReData}
        showDeReCases={true}
        notificationCounts={notificationCounts}
        notificationDeRe={notificationDeRe}
        notification={notification}
      />
      <CountsView
        heading={cfstConstants.offlineSchedulesHeading}
        counts={dashboardCountData?.nonCfst}
        notificationCounts={notificationCounts}
        notificationDeRe={notificationDeRe}
        notification={notification}
      />
      <ComparisionView
        notificationComparison={notificationComparison}
        dashboardComparisionData={dashboardComparisionData}
        setCfstComparisonViewFilterValue={setCfstComparisonViewFilterValue}
        jobTrendsNotification={jobTrendsNotification}
        onlineVsOfflineJobsTrendNotification={onlineVsOfflineJobsTrendNotification}
      />
    </SPWrapper>
  );
};

export default CFSTView;
